"use client";

import {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useRef,
  type ReactNode,
} from "react";
import { useStore } from "../store";

interface AuthContextValue {
  /** Whether auth state has been determined */
  ready: boolean;
  /** Whether the user is authenticated */
  authenticated: boolean;
  /** The device bearer token */
  token: string | null;
  /** The current user */
  user: AuthUser | null;
  /** True when the user just signed in (not a page refresh) */
  isNewLogin: boolean;
  /** Send OTP to phone number */
  sendOtp: (phone: string) => Promise<{ ok: boolean; error?: string }>;
  /** Verify OTP and log in */
  verifyOtp: (phone: string, code: string) => Promise<{ ok: boolean; error?: string }>;
  /** Update the current user profile */
  updateUser: (updates: { displayName?: string; avatarUrl?: string }) => Promise<{ ok: boolean; error?: string }>;
  /** Re-fetch user from the server (e.g. after claiming invites to refresh hasAccess) */
  refreshUser: () => Promise<void>;
  /** Clear the isNewLogin flag (called after onboarding completes) */
  clearNewLogin: () => void;
  /** Log out */
  logout: () => Promise<void>;
}

export interface AuthUser {
  id: string;
  phone: string;
  displayName: string | null;
  avatarUrl: string | null;
  hasAccess: boolean;
}

const AuthContext = createContext<AuthContextValue>({
  ready: false,
  authenticated: false,
  token: null,
  user: null,
  isNewLogin: false,
  sendOtp: async () => ({ ok: false }),
  verifyOtp: async () => ({ ok: false }),
  updateUser: async () => ({ ok: false }),
  refreshUser: async () => {},
  clearNewLogin: () => {},
  logout: async () => {},
});

export function useAuth() {
  return useContext(AuthContext);
}

const API_URL = process.env.NEXT_PUBLIC_API_URL ?? "/api";
const TOKEN_KEY = "occum_token";
const USER_KEY = "occum_user";

interface AuthState {
  ready: boolean;
  token: string | null;
  user: AuthUser | null;
  isNewLogin: boolean;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({
    ready: false,
    token: null,
    user: null,
    isNewLogin: false,
  });
  const stateRef = useRef(state);
  stateRef.current = state;

  // On mount: restore cached auth state from localStorage, then validate.
  // By reading both token AND cached user synchronously we skip the
  // blank → splash → content sequence entirely for returning users.
  useEffect(() => {
    const storedToken = localStorage.getItem(TOKEN_KEY);

    if (!storedToken) {
      setState({ ready: true, token: null, user: null, isNewLogin: false });
      return;
    }

    // Restore cached user immediately
    let cachedUser: AuthUser | null = null;
    try {
      const raw = localStorage.getItem(USER_KEY);
      if (raw) cachedUser = JSON.parse(raw);
    } catch {
      // corrupt cache, ignore
    }

    // Single state update — goes straight to authenticated view (NOT a new login)
    setState({ ready: true, token: storedToken, user: cachedUser, isNewLogin: false });

    // Validate token in the background; refresh user data or log out
    fetch(`${API_URL}/users/me`, {
      headers: { Authorization: `Bearer ${storedToken}` },
    })
      .then(async (res) => {
        if (res.ok) {
          const data = await res.json();
          const user: AuthUser = {
            id: data.id,
            phone: data.phone,
            displayName: data.displayName,
            avatarUrl: data.avatarUrl,
            hasAccess: data.hasAccess ?? false,
          };
          localStorage.setItem(USER_KEY, JSON.stringify(user));
          setState((prev) => ({ ...prev, user }));
        } else {
          localStorage.removeItem(TOKEN_KEY);
          localStorage.removeItem(USER_KEY);
          setState({ ready: true, token: null, user: null, isNewLogin: false });
        }
      })
      .catch(() => {
        // Network error — keep cached state, don't log user out
      });
  }, []);

  const sendOtp = useCallback(async (phone: string) => {
    try {
      const res = await fetch(`${API_URL}/auth/send-otp`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ phone }),
      });
      const body = await res.json();
      if (!res.ok) {
        return { ok: false, error: body.error ?? "Failed to send OTP" };
      }
      return { ok: true };
    } catch (err) {
      return { ok: false, error: (err as Error).message };
    }
  }, []);

  const verifyOtp = useCallback(async (phone: string, code: string) => {
    try {
      const res = await fetch(`${API_URL}/auth/verify-otp`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ phone, code }),
        credentials: "include",
      });
      const body = await res.json();
      if (!res.ok) {
        return { ok: false, error: body.error ?? "Invalid code" };
      }

      const user: AuthUser = {
        id: body.user.id,
        phone: body.user.phone,
        displayName: body.user.displayName,
        avatarUrl: body.user.avatarUrl,
        hasAccess: body.user.hasAccess ?? false,
      };

      // Single state update — token + user together, no intermediate renders
      localStorage.setItem(TOKEN_KEY, body.token);
      localStorage.setItem(USER_KEY, JSON.stringify(user));
      setState({ ready: true, token: body.token, user, isNewLogin: true });
      return { ok: true };
    } catch (err) {
      return { ok: false, error: (err as Error).message };
    }
  }, []);

  const updateUser = useCallback(async (updates: { displayName?: string; avatarUrl?: string }) => {
    const currentToken = stateRef.current.token;
    if (!currentToken) return { ok: false, error: "Not authenticated" };

    try {
      const res = await fetch(`${API_URL}/users/me`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${currentToken}`,
        },
        body: JSON.stringify(updates),
      });
      const body = await res.json();
      if (!res.ok) {
        return { ok: false, error: body.error ?? "Failed to update profile" };
      }

      const user: AuthUser = {
        id: body.id,
        phone: body.phone,
        displayName: body.displayName,
        avatarUrl: body.avatarUrl,
        hasAccess: body.hasAccess ?? stateRef.current.user?.hasAccess ?? false,
      };
      localStorage.setItem(USER_KEY, JSON.stringify(user));
      setState((prev) => ({ ...prev, user }));
      return { ok: true };
    } catch (err) {
      return { ok: false, error: (err as Error).message };
    }
  }, []);

  const refreshUser = useCallback(async () => {
    const currentToken = stateRef.current.token;
    if (!currentToken) return;

    try {
      const res = await fetch(`${API_URL}/users/me`, {
        headers: { Authorization: `Bearer ${currentToken}` },
      });
      if (res.ok) {
        const data = await res.json();
        const user: AuthUser = {
          id: data.id,
          phone: data.phone,
          displayName: data.displayName,
          avatarUrl: data.avatarUrl,
          hasAccess: data.hasAccess ?? false,
        };
        localStorage.setItem(USER_KEY, JSON.stringify(user));
        setState((prev) => ({ ...prev, user }));
      }
    } catch {
      // ignore
    }
  }, []);

  const clearNewLogin = useCallback(() => {
    setState((prev) => ({ ...prev, isNewLogin: false }));
  }, []);

  const logout = useCallback(async () => {
    const currentToken = stateRef.current.token;
    // Clear UI immediately — don't wait for the server round-trip
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    setState({ ready: true, token: null, user: null, isNewLogin: false });
    // Clear useHistoryState entries so stale steps (e.g. login-step: "code") don't persist
    window.history.replaceState({}, "");

    try {
      if (currentToken) {
        await fetch(`${API_URL}/auth/logout`, {
          method: "POST",
          headers: { Authorization: `Bearer ${currentToken}` },
          credentials: "include",
        });
      }
    } catch {
      // ignore
    }
  }, []);

  // Sync auth state to the Zustand store so store selectors work
  useEffect(() => {
    if (!state.ready) return;
    if (state.token && state.user) {
      useStore.getState().setAuth(state.token, state.user, state.isNewLogin);
    } else if (state.token) {
      useStore.getState().restoreAuth(state.token, null);
    } else {
      useStore.getState().clearAuth();
    }
  }, [state.ready, state.token, state.user, state.isNewLogin]);

  return (
    <AuthContext.Provider
      value={{
        ready: state.ready,
        authenticated: !!state.token && !!state.user,
        token: state.token,
        user: state.user,
        isNewLogin: state.isNewLogin,
        sendOtp,
        verifyOtp,
        updateUser,
        refreshUser,
        clearNewLogin,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}
